import React, {ReactElement, useEffect, useState} from 'react';

import {useWorkspaceContext} from '../../../../context/workspaceContext';
import {uuid} from '../../../../utils/utils';
import {ResourceType} from '../../../../@types/resource-type';
import {addOrUpdateUser, displayApiKey} from '../../../../utils/service-user-account-utils';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import Stack from '@mui/material/Stack';
import ErrorAlertComponent from '../../../../components/ErrorAlert.component';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import {toast} from 'react-toastify';
import {ServiceUserAccountEditorProps} from '../../../../@types/editor-props';
import ServiceUserAccount from '../../../../@types/service-user-account';
import serviceUserAccountService from '../../../../services/service-user-account.service';

export default function ServiceUserAccountEditor(props: ServiceUserAccountEditorProps): ReactElement {
    const { serviceUserAccount, updateTab } = props;
    const { workspaceObjects, setWorkspaceObjects, setCurrentWorkspaceObject, publishResourceUpdate } = useWorkspaceContext();

    const [userName, setUserName] = useState<string>();
    const [sourceName, setSourceName] = useState<string>();
    const [error, setError] = useState<string | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const uuidRef = React.useRef(uuid());


    const onUserNameChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        setUserName(event.target.value);
    };

    const onSourceNameChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        setSourceName(event.target.value);
    };

    const formServiceUserAccount = (): ServiceUserAccount => {
        const userData = {} as ServiceUserAccount;
        userData.username = userName ?? '';
        userData.sourceName = sourceName ?? '';

        // if (serviceUserAccount && serviceUserAccount.id) {
        //     userData.id = serviceUserAccount.id;
        //     userData.isActive = serviceUserAccount.isActive;
        // }
        return userData;
    }

    const updateUser = (usr: ServiceUserAccount): void => {
        let userObject = {objectType: ResourceType.SERVICE_USER_ACCOUNTS, object: usr};
        setCurrentWorkspaceObject(userObject);
        if (workspaceObjects?.objectType === ResourceType.SERVICE_USER_ACCOUNTS && workspaceObjects.resources) {
            setWorkspaceObjects({ objectType: ResourceType.SERVICE_USER_ACCOUNTS, resources: addOrUpdateUser(usr, workspaceObjects.resources as ServiceUserAccount[]) });
        }

        // Publish update event for other components
        publishResourceUpdate({
            type: ResourceType.SERVICE_USER_ACCOUNTS,
            id: usr.id,
            updatedData: usr,
            source: 'save'
        });

        updateTab(userObject);
    }

    const onClickSave = (_event: React.SyntheticEvent): void => {
        if (sourceName && userName) {
            setError(null);
            setIsRunning(true);
            serviceUserAccountService.createServiceUser(formServiceUserAccount()).then((usr: ServiceUserAccount) => {
                displayApiKey(usr);
                updateUser(usr);
                setEditMode(true);
                toast.success('Service User Account created successfully');
            }).catch((error: Error) => {
                setError(error.message);
            }).finally(() => {
                setIsRunning(false);
            });
        } else {
            setError('Please fill in all fields');
        }
    }
    useEffect(() => {
        setIsRunning(false);
        if (serviceUserAccount) {
            setUserName(serviceUserAccount.username);
            if (serviceUserAccount.id) {
                setEditMode(true);
            }
            // source Name is not set when you get a service user account from the API
            // we use then workspaceObjects to get the source name
            if (workspaceObjects && workspaceObjects.objectType === ResourceType.SERVICE_USER_ACCOUNTS) {
                const serviceUser: ServiceUserAccount | undefined = workspaceObjects?.resources?.find((usr: ServiceUserAccount) => usr.id === serviceUserAccount?.id);
                if (serviceUser) {
                    setSourceName(serviceUser.sourceName);
                }
            }            
        }        
    }, [serviceUserAccount]);

    return (
        <FormGroup className='formStyle' sx={{ p: 2 }}>
            <Stack className='formControl'>
                <Typography variant='h6' gutterBottom>{editMode ? 'Service User Account' : 'New Service User Account'}</Typography>
                <Stack direction='row' spacing={2} justifyContent='space-between'>
                    <Stack className='formControl'>
                        <TextField
                            id={`user-name-input-${uuidRef.current}`}
                            label='User Name'
                            aria-label='user name'
                            type='text'   
                            variant='outlined'
                            size='small'
                            autoComplete='on'
                            value={userName || ''}
                            disabled={editMode}
                            onChange={onUserNameChange}
                            sx={{
                                '& input': {
                                    border: 'none',
                                }
                            }}
                            slotProps={{
                                input: {
                                    'role': 'textbox',
                                    'tabIndex': 0,
                                    'aria-labelledby': `user-name-input-${uuidRef.current}`,
                                    'aria-label': 'user name'
                                }
                            }}
                        />
                        <hr />
                        <TextField
                            id={`source-name-input-${uuidRef.current}`}
                            label='Source Name'
                            aria-label='source name'
                            type='text'   
                            variant='outlined'
                            size='small'
                            autoComplete='on'
                            value={sourceName || ''}
                            disabled={editMode}
                            onChange={onSourceNameChange}
                            sx={{
                                '& input': {
                                    border: 'none',
                                }
                            }}
                            slotProps={{
                                input: {
                                    'role': 'textbox',
                                    'tabIndex': 0,
                                    'aria-labelledby': `source-name-input-${uuidRef.current}`,
                                    'aria-label': 'source name'
                                }
                            }}
                        />
                        <hr />
                        
                    
                        <div>
                            <Button className='formBtn' id={`save-user-${uuidRef.current}`}
                                size='small' variant='contained'
                                aria-label='save user'
                                title='Save User'
                                onClick={onClickSave}
                                startIcon={isRunning ? <CircularProgress size={20} /> : <SaveAltIcon />}
                                disabled={isRunning || editMode}
                            >Save</Button>
                        </div>                   
                        
                        {error ? <ErrorAlertComponent message={error} />  : null}
                    </Stack>
                </Stack>
            </Stack>
        </FormGroup>
    );
}
